import React, { useState, useEffect } from 'react';
import '../CrmModals.css';
import {
  FiFilter,
  FiX,
  FiCheck,
  FiCalendar,
  FiTag,
  FiActivity,
  FiAlertCircle,
  FiTrendingUp,
  FiRefreshCw
} from 'react-icons/fi';

const FilterModal = ({ stages, currentFilters, onApply, onClose }) => {
  const [filters, setFilters] = useState({
    stage_ids: currentFilters.stage_ids || [],
    sources: currentFilters.sources || [],
    priorities: currentFilters.priorities || [],
    statuses: currentFilters.statuses || [],
    date_from: currentFilters.date_from || '',
    date_to: currentFilters.date_to || '',
    tags: currentFilters.tags || []
  });

  const [tagInput, setTagInput] = useState('');


  const sourceOptions = [
    { value: 'MANUAL', label: 'Manual' },
    { value: 'FORM', label: 'Formulário' },
    { value: 'WHATSAPP', label: 'WhatsApp' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'WEBHOOK', label: 'Webhook' },
    { value: 'API', label: 'API' }
  ];

  const priorityOptions = [
    { value: 'LOW', label: 'Baixa', color: '#22c55e' },
    { value: 'MEDIUM', label: 'Média', color: '#f59e0b' },
    { value: 'HIGH', label: 'Alta', color: '#ef4444' },
    { value: 'URGENT', label: 'Urgente', color: '#dc2626' }
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Inativo' },
    { value: 'BLOCKED', label: 'Bloqueado' },
    { value: 'CONVERTED', label: 'Convertido' },
    { value: 'LOST', label: 'Perdido' }
  ];

  const handleToggle = (filterKey, value) => {
    setFilters(prev => {
      const currentValues = prev[filterKey] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterKey]: newValues };
    });
  };

  const handleDateChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !filters.tags.includes(tag)) {
      setFilters(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleClearAll = () => {
    setFilters({
      stage_ids: [],
      sources: [],
      priorities: [],
      statuses: [],
      date_from: '',
      date_to: '',
      tags: []
    });
  };

  const handleApply = () => {
    const cleanedFilters = {};
    Object.keys(filters).forEach(key => {
      if (Array.isArray(filters[key]) && filters[key].length > 0) {
        cleanedFilters[key] = filters[key];
      } else if (typeof filters[key] === 'string' && filters[key].trim() !== '') {
        cleanedFilters[key] = filters[key];
      }
    });
    onApply(cleanedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    Object.keys(filters).forEach(key => {
      if (Array.isArray(filters[key])) {
        count += filters[key].length;
      } else if (filters[key]) {
        count += 1;
      }
    });
    return count;
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal crm-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h2 className="crm-modal-title">
              <FiFilter className="crm-modal-title-icon" />
              Filtros Avançados
            </h2>
            <p className="crm-modal-subtitle">
              {getActiveFilterCount()} filtro(s) ativo(s) - Refine sua busca de leads
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="crm-modal-close"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <div className="crm-modal-body">
          <div className="crm-mb-8">
            <h3 className="crm-section-title">
              <FiActivity />
              Estágios do Pipeline
            </h3>
            <div className="crm-grid-responsive-sm">
              {stages.map(stage => (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => handleToggle('stage_ids', stage.id)}
                  className={`crm-chip-button crm-filter-chip ${filters.stage_ids.includes(stage.id) ? 'active' : ''}`}
                  style={{
                    '--chip-color': stage.color,
                    '--chip-bg': `${stage.color}20`
                  }}
                >
                  <span className="crm-stage-label">
                    <span
                      className="crm-color-dot"
                      style={{
                        background: stage.color
                      }}
                    />
                    {stage.name}
                  </span>
                  {filters.stage_ids.includes(stage.id) && <FiCheck />}
                </button>
              ))}
            </div>
          </div>

          <div className="crm-mb-8">
            <h3 className="crm-section-title">
              <FiTag />
              Origem do Lead
            </h3>
            <div className="crm-grid-responsive-xs">
              {sourceOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggle('sources', option.value)}
                  className={`crm-chip-button crm-filter-chip ${filters.sources.includes(option.value) ? 'active' : ''}`}
                >
                  {option.label}
                  {filters.sources.includes(option.value) && <FiCheck />}
                </button>
              ))}
            </div>
          </div>

          <div className="crm-mb-8">
            <h3 className="crm-section-title">
              <FiAlertCircle />
              Prioridade
            </h3>
            <div className="crm-grid-responsive-xs">
              {priorityOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggle('priorities', option.value)}
                  className={`crm-chip-button crm-filter-chip ${filters.priorities.includes(option.value) ? 'active' : ''}`}
                  style={{
                    '--chip-color': option.color,
                    '--chip-bg': `${option.color}20`
                  }}
                >
                  {option.label}
                  {filters.priorities.includes(option.value) && <FiCheck />}
                </button>
              ))}
            </div>
          </div>

          <div className="crm-mb-8">
            <h3 className="crm-section-title">
              <FiTrendingUp />
              Status
            </h3>
            <div className="crm-grid-responsive-xs">
              {statusOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggle('statuses', option.value)}
                  className={`crm-chip-button crm-filter-chip ${filters.statuses.includes(option.value) ? 'active' : ''}`}
                >
                  {option.label}
                  {filters.statuses.includes(option.value) && <FiCheck />}
                </button>
              ))}
            </div>
          </div>


          <div className="crm-mb-8">
            <h3 className="crm-section-title">
              <FiCalendar />
              Período de Criação
            </h3>
            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">Data Inicial</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleDateChange('date_from', e.target.value)}
                  className="crm-input"
                />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Data Final</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleDateChange('date_to', e.target.value)}
                  className="crm-input"
                />
              </div>
            </div>
          </div>

          <div className="crm-mb-8">
            <h3 className="crm-section-title">
              <FiTag />
              Filtrar por Tags
            </h3>
            <div className="crm-tag-input-wrapper">
              <div className="crm-tag-input-container">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  className="crm-input crm-tag-input"
                  placeholder="Digite uma tag e pressione Enter..."
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="crm-btn-icon"
                  disabled={!tagInput.trim()}
                >
                  <FiCheck />
                </button>
              </div>

              {filters.tags.length > 0 && (
                <div className="crm-tag-list">
                  {filters.tags.map(tag => (
                    <div key={tag} className="crm-tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="crm-tag-remove"
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="crm-modal-footer">
          <button
            type="button"
            onClick={handleClearAll}
            className="crm-btn crm-btn-secondary"
          >
            <FiRefreshCw />
            Limpar Tudo
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="crm-btn crm-btn-primary"
          >
            <FiCheck />
            Aplicar Filtros ({getActiveFilterCount()})
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
